import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  FullScreenLoading,
} from '@civilgrid/ui/components';
import { useParams } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { checkForSoilsRequest as apiCheckForSoilsRequest, soilsRequest as apiSoilsRequest } from '~/api/projects2';
import { Action } from '~/authorizations';
import Can from '~/authorizations/Can';
import EmailLink from '~/components2/EmailLink';
import { Notify } from '~/components2/Notify';
import { useIsSandboxOrg } from '~/hooks/useIsSandboxOrg';
import type { ProjectDetailsParams } from '../..';

interface OrderSoilsReportProps {
  queryResponse: GeoJSON.Feature[];
}

export default function OrderSoilsReport({ queryResponse }: OrderSoilsReportProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [isDisabled, setIsDisabled] = useState(true);
  const { projectId } = useParams({ strict: false }) as ProjectDetailsParams;
  const isSandboxOrg = useIsSandboxOrg();
  const [open, setOpen] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    (async () => {
      const res = await apiCheckForSoilsRequest(projectId);
      if (res.hasRequestedSoils === true || isSandboxOrg() === true) {
        setIsDisabled(true);
      } else {
        setIsDisabled(false);
      }

      setIsLoading(false);
    })();
  }, [projectId]);

  const orderReport = async () => {
    try {
      const featureIds = queryResponse.map((q) => q?.properties?.OBJECTID);

      const res = await apiSoilsRequest(projectId, featureIds);

      if (!res.ok) {
        throw new Error(`Error ordering soils report.`);
      }

      Notify.info({
        title: 'Soils Report Ordered',
        message: 'A CivilGrid representative will contact you to confirm soils report details, pricing, and delivery.',
      });
    } catch {
      Notify.error({
        title: 'Soils Report Error',
        message: 'There was an error ordering your soils report. Please contact support.',
      });
    }

    setOpen(false);
    setIsDisabled(true);
  };

  if (isLoading) {
    return <FullScreenLoading />;
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="geotech-order-soils-report-button">Order Report</Button>
      </DialogTrigger>
      <DialogContent
        title="Order Soils Report"
        className="z-[110] w-auto min-w-min max-w-[75vw]"
        overlayClassName="z-[100]"
        data-testid="geotech-order-soils-report-dialog"
      >
        <DialogBody className="text-sm">
          <div className="flex flex-col space-y-4">
            <p>
              Click{' '}
              <a
                className="cursor-pointer text-blue-50 underline hover:text-blue-60"
                href="/sample_soils_report.xlsx"
                target="_blank"
                rel="noopener noreferrer"
              >
                here
              </a>{' '}
              to download a sample Soils Report.
            </p>

            <p>This is only a sample report with representative data, it is not a report for this project location.</p>

            {isDisabled && (
              <p>
                A Soils Report has already been requested for this project. For additional Soils Reports, please{' '}
                <EmailLink subject="Soils Request Support">contact support</EmailLink>.
              </p>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Can action={Action.OrderFreeReports}>
            <Button disabled={isDisabled} onClick={orderReport} data-testid="geotech-order-soils-report-submit">
              Order Soils Report
            </Button>
          </Can>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
